import React from 'react';
import {
  useLoaderData,
  useRouter,
} from '@tanstack/react-router';
import { usePromiseModal } from '@prezly/react-promise-modal';
import { toast } from 'sonner';

import Pagination from '../../Pagination';
import PaginationHeader from '../../PaginationHeader';
import SearchField from '../../SearchField';
import DashboardAdminUser from './User';
import UserDeleteModal from './UserDeleteModal';
import Loading from '../../Loading';

import { useStore } from '../../../store';

import type { User } from '../../../types/user';

const DashboardAdminUsers: React.FC = () => {
  const searching = useStore.getState().searching;
  const { users: usersData } = useLoaderData({ strict: false });
  const users = usersData?.data || [];
  const totalUsers = usersData?.meta.total;
  const perPage = usersData?.meta.per_page;

  const router = useRouter();

  const deleteUser = usePromiseModal<boolean, { user: User }>(
    ({ show, onSubmit, onDismiss, user }) => (
      <UserDeleteModal
        user={user}
        show={show}
        onConfirm={(response: boolean) => onSubmit(response)}
        onDismiss={onDismiss}
      />
    )
  );

  const handleDeleteUser = async (user: User) => {
    const response = await deleteUser.invoke({ user }) as boolean;
    if (response === true) {
      toast.success('User deleted successfully');
      router.navigate({
        to: '.',
        search: (prev: any) => ({
          ...prev,
          t: new Date().getTime(),
        }),
      });
      router.invalidate({
        filter: (route) => {
          return route.routeId === window.location.pathname;
        },
      });
      setTimeout(() => {
        router.navigate({
          to: '.',
          search: (prev: any) => ({
            ...prev,
            t: undefined,
          }),
        });
      }, 100);
    } else if (response === false) {
      toast.error('Failed to delete user');
    }
  };

  return (
    <>
      <div className="dashboard-header page-header">
        <h2 className="page-title">All <em>users</em></h2>
        <p>All registered users</p>
      </div>
      <div className="dashboard-admin dashboard-admin-users wa-stack wa-gap-0 inner">
        <div className="wa-stack">
          <div className="total-items">
            <PaginationHeader text="User" total={totalUsers} perPage={perPage} />
          </div>
          <SearchField />
        </div>
        {searching ? (
          <Loading internal />
        ) : (
          <div className="dashboard-admin-users-list wa-stack">
            <table>
              <thead>
                <tr>
                  <th>Signed up</th>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Plan</th>
                  <th>SPRKs</th>
                  <th>Memories</th>
                  <th>Timelines</th>
                  <th>Shares</th>
                  <th className="actions">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user: User) => (
                  <DashboardAdminUser
                    key={user.id}
                    user={user}
                    handleDeleteUser={handleDeleteUser}
                  />
                ))}
              </tbody>
            </table>
            <Pagination total={totalUsers} perPage={perPage} />
          </div>
        )}
      </div>
      {deleteUser.modal}
    </>
  );
}

export default DashboardAdminUsers;
